import { useEffect, useMemo, useRef, useState } from "react";
import {
  lerFiltros,
  lerNotas,
  GAVETAS_DE_PRAZO,
  ROTULO_GAVETA_DE_PRAZO,
  gavetaDaQuery,
  type FiltrosDoAdminPos,
  type GavetaDePrazo,
  type NotaDoAdmin,
  type OrdemDasNotas,
} from "../lib/adminPos";
import { CampoDeBusca } from "./ui";
import { BarraDeFiltros, CampoDeData, Filtro, LimparFiltros, LinhaDeFiltros } from "./BarraDeFiltros";
import Carregando from "./Carregando";
import ModalCorrigirNota from "./ModalCorrigirNota";

// Admin › Faculdade › Notas — as tentativas da pós, para a equipe.
//
// Pedido da reunião de 31/08: *"painel para cuidar das notas e tentativas (com
// filtros: curso, turma, avaliação, ordenação de maior nota, ordenação A-Z de
// avaliação)"*.
//
// ---------------------------------------------------------------------------
// O filtro é do SERVIDOR, e isso não é detalhe de implementação
// ---------------------------------------------------------------------------
// `pos_tentativas` tem **246.529** linhas. Trazer tudo e filtrar em JavaScript
// seria a fila de documentos de novo, que saía com 41.910px de página. Aqui o
// filtro, a ordenação e o corte são do banco (`pos_admin_notas`, 000143), e cada
// linha carrega `total_no_filtro` — é ele que faz o cabeçalho dizer "50 de 4.312"
// em vez de mentir por omissão.
//
// ---------------------------------------------------------------------------
// A BUSCA é por aluno, porque o caso real é o aluno LIGANDO
// ---------------------------------------------------------------------------
// Mesma descoberta da fila de documentos, onde a busca entrou na 000133 depois de
// o teste mostrar que um operador atendendo UMA pessoa não tinha como achar o
// documento dela entre 12.625. Aqui é pior: 246 mil tentativas.
//
// ---------------------------------------------------------------------------
// A nota do Bubble aparece quando existe
// ---------------------------------------------------------------------------
// São **184** tentativas com nota acima de 10 na origem — defeito do Bubble,
// confirmado por Victor. A carga deixou `nota` nula e guardou o valor original em
// `nota_bubble`, em vez de aparar para 10 (que inventaria uma nota que ninguém
// deu) ou descartar (que perderia o registro da tentativa). Esta tela é a única
// que mostra esse número, porque é a equipe que vai decidir o que fazer com eles.

const POR_PAGINA = 50;

export default function AdminPosNotas() {
  const [filtros, setFiltros] = useState<FiltrosDoAdminPos | null>(null);
  const [linhas, setLinhas] = useState<NotaDoAdmin[]>([]);
  const [fase, setFase] = useState<"carregando" | "pronto" | "erro">("carregando");
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Qual linha está sendo corrigida (000243). Guarda a LINHA inteira, não o id:
  // o modal mostra o aluno, a disciplina e a nota atual, e buscá-los de novo
  // seria uma ida ao banco para dado que a tabela já tem na mão.
  const [corrigindo, setCorrigindo] = useState<NotaDoAdmin | null>(null);
  const [feito, setFeito] = useState<string | null>(null);
  // Bump depois de corrigir: a tabela relê, porque a nota e o selo de aprovada
  // mudaram. Alterar a linha em memória seria uma segunda definição de
  // "aprovada" — quem decide é o banco, pela MAIOR nota do aluno.
  const [versao, setVersao] = useState(0);

  const [curso, setCurso] = useState("");
  const [turma, setTurma] = useState("");
  const [disciplina, setDisciplina] = useState("");
  const [busca, setBusca] = useState("");
  const [ordem, setOrdem] = useState<OrdemDasNotas>("recente");
  // 000206: o período (sobre a data que a tela mostra) e o prazo da matrícula.
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [prazo, setPrazo] = useState<GavetaDePrazo | null>(null);
  const [pagina, setPagina] = useState(0);

  // A busca não dispara a cada tecla: 246 mil linhas com `ilike '%x%'` por
  // caractere digitado é carga desnecessária no banco de produção. 400 ms é o
  // intervalo em que a pessoa termina de digitar um nome.
  const [buscaAtiva, setBuscaAtiva] = useState("");
  const relogio = useRef<number | null>(null);
  useEffect(() => {
    if (relogio.current) window.clearTimeout(relogio.current);
    relogio.current = window.setTimeout(() => setBuscaAtiva(busca), 400);
    return () => {
      if (relogio.current) window.clearTimeout(relogio.current);
    };
  }, [busca]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const f = await lerFiltros();
        if (!vivo) return;
        setFiltros(f);
      } catch {
        if (vivo) setFase("erro");
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  // Trocar qualquer filtro volta à primeira página. Continuar na página 5 de um
  // filtro novo mostraria uma tela vazia com paginação ativa — e o operador
  // concluiria que não há resultado.
  useEffect(() => {
    setPagina(0);
  }, [curso, turma, disciplina, buscaAtiva, ordem, de, ate, prazo]);

  useEffect(() => {
    let vivo = true;
    setBuscando(true);
    (async () => {
      try {
        const r = await lerNotas({
          curso: curso || null,
          turma: turma || null,
          disciplina: disciplina || null,
          busca: buscaAtiva || null,
          ordem,
          limite: POR_PAGINA,
          offset: pagina * POR_PAGINA,
          de: de || null,
          ate: ate || null,
          prazo,
        });
        if (!vivo) return;
        setLinhas(r);
        setErro(null);
        setFase("pronto");
      } catch (e) {
        if (!vivo) return;
        // Erro de FILTRO não apaga a tela: a peça que falha é que informa, e o
        // operador continua vendo o resultado anterior com o aviso ao lado. É a
        // lição do Calendário, onde um tique que não gravou trocava o mês inteiro
        // pela mensagem de "não conseguimos carregar".
        setErro(e instanceof Error ? e.message : "não deu para carregar as notas");
        setFase("pronto");
      } finally {
        if (vivo) setBuscando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [curso, turma, disciplina, buscaAtiva, ordem, pagina, de, ate, prazo, versao]);

  // As turmas e as disciplinas do curso escolhido. Sem isto o operador da CGT
  // escolheria entre 58 turmas e 233 disciplinas de oito cursos diferentes.
  // A lista de turmas NÃO existe antes do curso — regra de Victor em 14/09:
  // *"jamais mostrar a lista de turmas sem antes selecionar o curso"*. A versão
  // anterior filtrava com `!curso ||`, o que mostrava as **59 turmas** quando
  // nenhum curso estava escolhido.
  //
  // O número que faz a regra ser regra: as 59 usam só **20 números**, e "Turma
  // 1" existe em **9 cursos**. Como o rótulo é `Turma <n> · <m> matriculados`,
  // sem o curso a lista oferece nove opções com o mesmo nome — o operador não
  // tem como escolher, e o que ele escolher vai parecer certo.
  const turmasDoCurso = useMemo(
    () => (curso ? (filtros?.turmas ?? []).filter((t) => t.curso_slug === curso) : []),
    [filtros, curso],
  );
  // Mesma regra da turma, e pelo mesmo motivo de escala: são **245**
  // disciplinas em oito cursos, e nomes como "Metodologia Científica" se
  // repetem entre eles.
  const discsDoCurso = useMemo(
    () => (curso ? (filtros?.disciplinas ?? []).filter((d) => d.curso_slug === curso) : []),
    [filtros, curso],
  );

  // Trocar de curso limpa turma e disciplina: uma turma da CGT selecionada com o
  // curso da Perícia devolveria zero linhas, e o operador leria isso como "esta
  // turma não tem nota".
  function trocarCurso(novo: string) {
    setCurso(novo);
    setTurma("");
    setDisciplina("");
  }

  if (fase === "carregando" && !filtros) return <Carregando />;
  if (fase === "erro") {
    return (
      <p className="rounded-xl border border-erro-borda bg-erro-fundo p-4 text-sm text-erro-tinta">
        Não conseguimos abrir o painel de notas. Recarregue a página.
      </p>
    );
  }
  if (filtros === null) {
    return (
      <p className="rounded-xl border border-borda bg-superficie p-4 text-sm text-cinza">
        Este painel é da equipe da secretaria. Se você deveria vê-lo, fale com o Victor.
      </p>
    );
  }

  const total = linhas[0]?.total_no_filtro ?? 0;
  const primeira = pagina * POR_PAGINA;

  return (
    // O espaçamento entre os blocos é do CONTAINER, e é o MESMO das três
    // sub-abas (`gap-5`, como o balcão em `SuporteFaculdade.tsx`).
    //
    // Antes cada bloco levava a própria margem (`mb-4` no cabeçalho, `mb-3` no
    // aviso, `mt-4` na paginação) e a barra de filtros — que não tem margem, de
    // propósito, porque é peça compartilhada — ficava GRUDADA na tabela. Victor
    // viu na tela em 09/09: "o box dos filtros ficou grudado no box da tabela,
    // não ficou com espaçamento alinhado igual na tela de documentos".
    //
    // Margem por bloco é a mesma decisão escrita em N lugares: quem esquecer uma
    // produz exatamente este defeito, e o esquecido é sempre o par que ninguém
    // olha junto. Com o gap no container, acrescentar um bloco novo não pede
    // decisão nenhuma.
    <section className="flex flex-col gap-5">
      <header>
        <h2 className="text-lg font-bold text-titulo">Notas e tentativas</h2>
        <p className="mt-1 text-sm text-cinza">
          {/* O total do FILTRO, sempre. "50 de 50" e "50 de 246.529" pedem
              trabalho diferente do operador. */}
          {buscando && linhas.length === 0
            ? "Buscando…"
            : total === 0
              ? "Nenhuma tentativa com esses filtros."
              : `${primeira + 1}–${primeira + linhas.length} de ${total.toLocaleString("pt-BR")} tentativa${total === 1 ? "" : "s"}`}
        </p>
      </header>

      {/* A MESMA barra do balcão (000206), com os dois filtros que Victor pediu
          para as três sub-abas: o período e o prazo. Antes era uma grade de
          cinco combos com rótulo em cima — a informação é a mesma, e o desenho
          é o de `BarraDeFiltros.tsx`, para as três telas da aba não terem três
          formas.

          O rótulo de cada combo mora na opção "todos" ("Todos os cursos"), que
          nomeia o filtro e diz o que ele faz quando ninguém escolheu; o nome
          acessível vai no `aria-label`. Sem contagem nas opções, pela decisão de
          09/09: ela era da base inteira e não acompanhava os outros filtros. */}
      <BarraDeFiltros>
        <LinhaDeFiltros n={1}>
          <CampoDeBusca
            valor={busca}
            aoMudar={setBusca}
            placeholder="Buscar por nome ou e-mail"
            rotulo="Buscar aluno por nome ou e-mail"
            aoLimpar={busca ? () => setBusca("") : undefined}
            data-filtro="busca"
            className="min-w-[18rem] flex-1"
          />
          <Filtro
            rotulo="Curso"
            rotuloTodos="Todos os cursos"
            chave="curso"
            valor={curso || null}
            aoTrocar={(v) => trocarCurso(v ?? "")}
            opcoes={filtros.cursos.map((c) => ({ valor: c.slug, rotulo: c.nome ?? c.slug }))}
          />
          <Filtro
            rotulo="Turma"
            rotuloTodos={curso ? "Todas as turmas" : "Escolha o curso primeiro"}
            desligado={!curso}
            chave="turma"
            valor={turma || null}
            aoTrocar={(v) => setTurma(v ?? "")}
            opcoes={turmasDoCurso.map((t) => ({
              valor: t.id,
              // A contagem de matriculados fica: ela não é "quantos itens este
              // filtro devolve" (o número que saiu dos combos), é o TAMANHO da
              // turma — o que faz a operadora distinguir a turma cheia da que
              // teve dois alunos. Ele não muda com os outros filtros porque não
              // é sobre eles.
              rotulo: `Turma ${t.numero} · ${t.matriculados} matriculado${t.matriculados === 1 ? "" : "s"}`,
            }))}
          />
          <Filtro
            rotulo="Avaliação"
            rotuloTodos={curso ? "Todas as avaliações" : "Escolha o curso primeiro"}
            desligado={!curso}
            chave="disciplina"
            valor={disciplina || null}
            aoTrocar={(v) => setDisciplina(v ?? "")}
            opcoes={discsDoCurso.map((d) => ({ valor: d.id, rotulo: d.nome }))}
          />
        </LinhaDeFiltros>

        <LinhaDeFiltros n={2}>
          {/* O período é sobre a data que a coluna "Data" mostra —
              `finalizada_em`, com `iniciada_em` só para a tentativa que não
              terminou. Sobre `iniciada_em` sozinha ele não separaria nada: as
              246.529 tentativas têm UM único dia ali (31/08/2026, a data da
              carga), contra 523 dias em `finalizada_em`. */}
          <CampoDeData
            rotulo="Tentativa a partir de"
            chave="de"
            valor={de || null}
            min={filtros.periodo_notas?.de}
            max={ate || filtros.periodo_notas?.ate}
            aoTrocar={(v) => setDe(v ?? "")}
          />
          <CampoDeData
            rotulo="Tentativa até"
            chave="ate"
            valor={ate || null}
            min={de || filtros.periodo_notas?.de}
            max={filtros.periodo_notas?.ate}
            aoTrocar={(v) => setAte(v ?? "")}
          />
          {/* O prazo é o da MATRÍCULA do aluno naquele curso — prova não tem
              prazo. Aqui ele responde "as tentativas de quem está fora do prazo
              do curso", que é pergunta de balcão: medido em 09/09, 60.963 das
              246.529 tentativas são de quem já estourou o máximo. E é
              informação, não trava: o máximo nunca impediu certificado
              (000144). */}
          <Filtro
            rotulo="Prazo do curso"
            rotuloTodos="Todos os prazos"
            chave="prazo"
            valor={prazo}
            aoTrocar={(v) => setPrazo(gavetaDaQuery(v))}
            opcoes={GAVETAS_DE_PRAZO.map((g) => ({ valor: g, rotulo: ROTULO_GAVETA_DE_PRAZO[g] }))}
          />
          {/* Ordenar não é filtrar, e fica na barra de todo modo: é o outro
              controle da MESMA lista, e o operador procura os dois no mesmo
              lugar. O rótulo diz o que a opção faz, sem "Todos". */}
          <Filtro
            rotulo="Ordenar por"
            rotuloTodos="Mais recente"
            chave="ordem"
            valor={ordem === "recente" ? null : ordem}
            aoTrocar={(v) => setOrdem((v as OrdemDasNotas | null) ?? "recente")}
            opcoes={[
              { valor: "nota", rotulo: "Maior nota" },
              { valor: "avaliacao", rotulo: "Avaliação (A-Z)" },
            ]}
          />
        </LinhaDeFiltros>

        {(curso || turma || disciplina || busca || de || ate || prazo || ordem !== "recente") && (
          <LimparFiltros
            aoLimpar={() => {
              setCurso("");
              setTurma("");
              setDisciplina("");
              setBusca("");
              setDe("");
              setAte("");
              setPrazo(null);
              setOrdem("recente");
            }}
          />
        )}
      </BarraDeFiltros>

      {erro && (
        <p className="rounded-lg border border-aviso-borda bg-aviso-fundo p-3 text-sm text-aviso-tinta">
          {erro} — a lista abaixo é a da consulta anterior.
        </p>
      )}

      {/* O resultado da correção (000243). Família `aviso`, que nesta casa é
          VERDE — confirmação —, e aqui ela é a certa: a nota foi corrigida. A
          frase vem do BANCO, e ela diz o ESTADO NOVO do aluno ("a melhor nota
          agora é 9,0 — APROVADO"), não "salvo". */}
      {feito && (
        <p
          data-correcao-feita
          className="rounded-lg border border-aviso-borda bg-aviso-fundo p-3 text-sm text-aviso-tinta"
        >
          {feito}
        </p>
      )}

      {corrigindo && (
        <ModalCorrigirNota
          linha={corrigindo}
          aberto={corrigindo !== null}
          aoFechar={() => setCorrigindo(null)}
          aoCorrigir={(texto) => {
            setFeito(texto);
            setVersao((v) => v + 1);
          }}
        />
      )}

      <div className="overflow-x-auto rounded-xl border border-borda bg-superficie">
        <table data-tabela-notas className="w-full min-w-[52rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-borda text-left">
              <th scope="col" className="p-3 font-semibold text-tinta">Aluno</th>
              <th scope="col" className="p-3 font-semibold text-tinta">Avaliação</th>
              <th scope="col" className="p-3 font-semibold text-tinta">Turma</th>
              <th scope="col" className="p-3 font-semibold text-tinta">Tent.</th>
              <th scope="col" className="p-3 text-right font-semibold text-tinta">Nota</th>
              <th scope="col" className="p-3 font-semibold text-tinta">Data</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => (
              <tr
                key={l.id}
                data-nota-linha={l.id}
                className="border-b border-borda/60 last:border-0"
              >
                <td className="p-3">
                  <span className="block font-semibold text-tinta">{l.aluno_nome ?? "—"}</span>
                  <span className="block text-xs text-cinza">{l.aluno_email}</span>
                </td>
                <td className="p-3">
                  <span className="block text-tinta">{l.disciplina}</span>
                  {/* O nome do curso só quando ele MUDA, e nunca quando o filtro
                      já o fixou.

                      Medido na captura: com o filtro em "Todos", a primeira
                      página mostrava "MBA em Empreendedorismo, Marketing e Gestão
                      Contábil" **50 vezes** — 60 caracteres repetidos em cada
                      linha, que é a cicatriz do IR Pro ("quando a tela parece
                      cheia e não diz nada a mais, conte quantas vezes a mesma
                      informação aparece"). Repetido, o nome deixa de informar e
                      passa a esconder a disciplina, que é o que se lê aqui. */}
                  {!curso && (i === 0 || linhas[i - 1].curso_slug !== l.curso_slug) && (
                    <span className="block text-xs text-cinza">{l.curso_nome ?? l.curso_slug}</span>
                  )}
                </td>
                <td className="p-3 whitespace-nowrap text-cinza">
                  {l.turma_numero !== null ? `Turma ${l.turma_numero}` : "—"}
                </td>
                <td className="p-3 tabular-nums text-cinza">{l.numero}ª</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {l.nota !== null ? (
                    <>
                      <span
                        data-nota-valor={l.nota}
                        className="font-display font-bold tabular-nums text-titulo"
                      >
                        {l.nota.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}
                      </span>
                      {l.aprovada !== null && (
                        <span
                          className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${
                            l.aprovada
                              ? "bg-aviso-fundo text-sucesso"
                              : "bg-erro-fundo text-erro-tinta"
                          }`}
                        >
                          {l.aprovada ? "aprovada" : "abaixo de 6"}
                        </span>
                      )}
                    </>
                  ) : l.nota_bubble !== null ? (
                    // Uma das 184. O número original fica à vista porque é a
                    // única informação que existe sobre aquela tentativa — e é a
                    // equipe que vai decidir o que fazer com ele.
                    <span
                      data-nota-bubble={l.nota_bubble}
                      className="rounded-full bg-aviso-fundo px-2 py-0.5 text-xs font-semibold text-aviso-tinta"
                      title="A origem gravou uma nota impossível (acima de 10). O hub guardou o valor e não o converteu."
                    >
                      Bubble: {l.nota_bubble.toLocaleString("pt-BR")}
                    </span>
                  ) : (
                    <span className="text-cinza">—</span>
                  )}

                  {/* ── Corrigir (000243) ─────────────────────────────────────
                      Só em tentativa FINALIZADA: em andamento não há nota para
                      corrigir, e `pos_tentativa_coerente` proíbe nota com
                      situação `em_andamento`. A RPC recusa com frase, mas
                      oferecer o botão seria convidar ao erro.

                      Um link discreto e não um botão por linha: são 25 linhas na
                      tela, e 25 botões de ação é a cicatriz do balcão. */}
                  {l.situacao === "finalizada" && (
                    <button
                      type="button"
                      onClick={() => setCorrigindo(l)}
                      data-corrigir-nota={l.id}
                      className="mt-1 block w-full text-right text-xs font-semibold text-link underline decoration-link/40 underline-offset-2 hover:decoration-link"
                    >
                      corrigir
                    </button>
                  )}
                </td>
                <td className="p-3 whitespace-nowrap text-cinza">
                  {l.situacao === "em_andamento" ? (
                    <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-tinta">
                      em andamento
                    </span>
                  ) : l.finalizada_em ? (
                    new Date(l.finalizada_em).toLocaleDateString("pt-BR")
                  ) : (
                    // Abandonada CONSOME tentativa, e sem esta palavra o operador
                    // não entende por que o aluno tem 5 usadas e 3 notas.
                    <span>
                      {new Date(l.iniciada_em).toLocaleDateString("pt-BR")}
                      <span className="block text-xs">não finalizada</span>
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {linhas.length === 0 && !buscando && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-sm text-cinza">
                  Nenhuma tentativa com esses filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação por PÁGINA e não "mostrar mais": aqui o operador procura um
          caso, não lê a lista inteira, e voltar é tão comum quanto avançar. */}
      {total > POR_PAGINA && (
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            data-pagina-anterior
            disabled={pagina === 0 || buscando}
            onClick={() => setPagina((p) => Math.max(0, p - 1))}
            className="btn-contorno text-sm disabled:cursor-not-allowed disabled:opacity-45"
          >
            ← Anteriores
          </button>
          <span className="text-xs text-cinza">
            página {pagina + 1} de {Math.max(1, Math.ceil(total / POR_PAGINA))}
          </span>
          <button
            type="button"
            data-pagina-proxima
            disabled={primeira + linhas.length >= total || buscando}
            onClick={() => setPagina((p) => p + 1)}
            className="btn-contorno text-sm disabled:cursor-not-allowed disabled:opacity-45"
          >
            Próximas →
          </button>
        </div>
      )}
    </section>
  );
}
